import React, {useState, useEffect, useRef} from "react";
import { Button } from "@/ui-components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/ui-components/ui/dialog";
import { Input } from "@/ui-components/ui/input";
import { Checkbox } from "@/ui-components/ui/checkbox";
import { PermissionFeatureWrapper } from "@/util-components/auth/PermissionFeatureWrapper";
import { Permission } from "@/lib/api/interfaces/permissions.interface";
import { PlanKey } from "@/lib/api/interfaces/billing.interface";

export function Discover() {
    // Statische Mock-Daten für das visuelle Dummy
    const kpis = [
        { label: 'Restpotenzial', value: '€ 184.500', trend: '+12%', trendType: 'up', spark: [120,132,128,140,150,160,158,170] },
        { label: 'Pipeline Wert', value: '€ 62.300', trend: '+4%', trendType: 'up', spark: [50,55,60,58,59,61,62,63] },
        { label: 'Aktivierungsquote', value: '68%', trend: '-3%', trendType: 'down', spark: [72,71,70,69,68,67,68,66] },
        { label: 'Branchen-Diversität', value: '0.72', trend: '+0.05', trendType: 'up', spark: [0.65,0.66,0.67,0.68,0.69,0.70,0.71,0.72] },
    ];

    const leads = [
        {
            name: 'Müller Energie GmbH',
            industry: 'Energie',
            estValue: '€ 18.000',
            score: 86,
            tags: ['Regional', 'Kategorie frei', 'Hohe Aktivierung'],
        },
        {
            name: 'FreshBite Foods',
            industry: 'Food',
            estValue: '€ 9.500',
            score: 74,
            tags: ['Event-Saison', 'Social stark'],
        },
        {
            name: 'Alpha IT Solutions',
            industry: 'IT',
            estValue: '€ 22.000',
            score: 91,
            tags: ['Wachstum', 'Digitale Affinität', 'Exklusivität'],
        },
        {
            name: 'VitaHealth AG',
            industry: 'Healthcare',
            estValue: '€ 13.200',
            score: 68,
            tags: ['CSR Fit'],
        },
        {
            name: 'GreenRide Mobility',
            industry: 'Mobilität',
            estValue: '€ 15.800',
            score: 79,
            tags: ['Nachhaltigkeit', 'Storytelling'],
        },
    ];

    const recommendations = [
        {
            title: 'Upgrade: Hospitality für Müller Energie',
            impact: 'Ø +€ 4.000',
            rationale: ['Unterauslastung VIP', 'Hoher Aktivierungsgrad'],
            type: 'UPSELL',
        },
        {
            title: 'Lead push: Alpha IT vor Tech-Messe',
            impact: 'Ø +€ 22.000',
            rationale: ['Timing Fenster 30 Tage', 'Exklusivitätschance'],
            type: 'LEAD',
        },
        {
            title: 'Reaktivierung: FreshBite Social Bundle',
            impact: 'Ø +€ 3.500',
            rationale: ['Social Paket frei', 'Food-Content performt'],
            type: 'ACTIVATION',
        },
    ];

    function scoreColor(score: number) {
        if (score >= 85) return 'bg-green-600';
        if (score >= 70) return 'bg-emerald-500';
        if (score >= 55) return 'bg-yellow-400';
        return 'bg-red-500';
    }

    function Sparkline({ data, color, id }: { data: number[]; color: string; id: string }) {
        const h = 46;
        const containerRef = useRef<HTMLDivElement>(null);
        const [w, setW] = useState(240);
        useEffect(() => {
            function measure() {
                if (containerRef.current) {
                    setW(containerRef.current.clientWidth);
                }
            }
            measure();
            window.addEventListener('resize', measure);
            return () => window.removeEventListener('resize', measure);
        }, []);
        if (!data || data.length < 2) {
            return <div className="h-10 flex items-center justify-center text-[10px] text-gray-400">Keine Daten</div>;
        }
        const min = Math.min(...data);
        const max = Math.max(...data);
        const range = max - min || 1;
        const points = data.map((v, i) => {
            const x = (i / (data.length - 1)) * w;
            const y = h - ((v - min) / range) * (h - 8) - 4;
            return { x, y, v };
        });
        const pathD = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' ');
        const areaD = `${pathD} L${points[points.length - 1].x},${h} L0,${h} Z`;
        // eslint-disable-next-line react-hooks/rules-of-hooks
        const [hoverIdx, setHoverIdx] = useState<number | null>(null);
        const active = hoverIdx != null ? points[hoverIdx] : null;
        const tooltipLeftPct = active ? Math.min(92, Math.max(8, (active.x / w) * 100)) : 0;
        return (
            <div ref={containerRef} className="relative group w-full">
                <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-12" preserveAspectRatio="none" role="img" aria-label="Trend Mini-Chart">
                    <defs>
                        <linearGradient id={`grad-${id}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                            <stop offset="80%" stopColor={color} stopOpacity={0.05} />
                            <stop offset="100%" stopColor={color} stopOpacity={0} />
                        </linearGradient>
                        <filter id={`glow-${id}`} x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                            <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                            </feMerge>
                        </filter>
                    </defs>
                    <line x1={0} y1={h - 1} x2={w} y2={h - 1} stroke="#E5E7EB" strokeWidth={1} />
                    <path d={areaD} fill={`url(#grad-${id})`} />
                    <path d={pathD} fill="none" stroke={color} strokeWidth={2.2} strokeLinecap="round" filter={`url(#glow-${id})`} />
                    {points.map((p, i) => (
                        <circle
                            key={i}
                            cx={p.x}
                            cy={p.y}
                            r={hoverIdx === i ? 3.8 : 2.2}
                            fill={color}
                            className="transition-all duration-150 cursor-pointer"
                            onMouseEnter={() => setHoverIdx(i)}
                            onMouseLeave={() => setHoverIdx(null)}
                        />
                    ))}
                </svg>
                {active && (
                    <div
                        className="absolute z-10 -translate-x-1/2 rounded-md bg-white/90 backdrop-blur px-2 py-1 shadow border border-gray-200 text-[10px] text-gray-700"
                        style={{ left: `${tooltipLeftPct}%`, top: 0 }}
                    >
                        <div className="font-medium">{active.v}</div>
                        <div className="text-[9px] uppercase tracking-wide text-gray-400">Trendpunkt</div>
                    </div>
                )}
            </div>
        );
    }

    function RadarChart() {
        const categories = [
            { name: 'Finanzen', value: 0.15 },
            { name: 'Versicherung', value: 0.1 },
            { name: 'Telekom', value: 0.3 },
            { name: 'Education', value: 0.05 },
            { name: 'Logistik', value: 0.2 },
            { name: 'Bau', value: 0.12 },
        ];
        const size = 280; // vergrößert
        const radius = size / 2 - 24;
        const center = size / 2;
        const angleStep = (Math.PI * 2) / categories.length;
        const [hoverIdx, setHoverIdx] = useState<number | null>(null);
        function pointFor(i: number, val: number) {
            const angle = i * angleStep - Math.PI / 2;
            const r = val * radius;
            return { x: center + r * Math.cos(angle), y: center + r * Math.sin(angle) };
        }
        const polyPoints = categories.map((c, i) => {
            const { x, y } = pointFor(i, c.value);
            return `${x},${y}`;
        }).join(' ');
        const gridLevels = 5;
        const grids = Array.from({ length: gridLevels }, (_, lvl) => {
            const r = ((lvl + 1) / gridLevels) * radius;
            const pts = categories.map((_, i) => {
                const angle = i * angleStep - Math.PI / 2;
                const x = center + r * Math.cos(angle);
                const y = center + r * Math.sin(angle);
                return `${x},${y}`;
            }).join(' ');
            return <polygon key={lvl} points={pts} fill="none" stroke="#E5E7EB" strokeWidth={0.6} />;
        });
        return (
            <div className="flex flex-col items-center gap-3 w-full">
                <div className="relative w-full max-w-[320px] mx-auto">
                    <svg viewBox={`0 0 ${size} ${size}`} className="w-full h-auto" aria-label="Branchen Radar">
                        <defs>
                            <radialGradient id="radar-fill" cx="50%" cy="50%" r="50%">
                                <stop offset="0%" stopColor="#6366F1" stopOpacity={0.35} />
                                <stop offset="70%" stopColor="#6366F1" stopOpacity={0.12} />
                                <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                            </radialGradient>
                        </defs>
                        {grids}
                        <polygon points={polyPoints} fill="url(#radar-fill)" stroke="#4F46E5" strokeWidth={2} />
                        {categories.map((c, i) => {
                            const { x, y } = pointFor(i, c.value);
                            const opportunity = 1 - c.value;
                            const color = opportunity > 0.8 ? '#dc2626' : opportunity > 0.6 ? '#f97316' : opportunity > 0.4 ? '#eab308' : '#16a34a';
                            return (
                                <circle
                                    key={c.name}
                                    cx={x}
                                    cy={y}
                                    r={hoverIdx === i ? 7 : 5.2}
                                    fill={color}
                                    className="cursor-pointer transition-all duration-150"
                                    onMouseEnter={() => setHoverIdx(i)}
                                    onMouseLeave={() => setHoverIdx(null)}
                                />
                            );
                        })}
                        {categories.map((c, i) => {
                            const angle = i * angleStep - Math.PI / 2;
                            const labelR = radius + 18;
                            const lx = center + labelR * Math.cos(angle);
                            const ly = center + labelR * Math.sin(angle);
                            return (
                                <text key={c.name + '-label'} x={lx} y={ly} fontSize={11} textAnchor="middle" fill="#374151" dy={ly < center ? -6 : 14}>
                                    {c.name}
                                </text>
                            );
                        })}
                    </svg>
                    {hoverIdx != null && (
                        <div className="absolute left-1/2 top-2 -translate-x-1/2 bg-white/90 backdrop-blur rounded-md border border-gray-200 shadow px-3 py-2 text-xs text-gray-700">
                            <div className="font-semibold">{categories[hoverIdx].name}</div>
                            <div className="flex gap-2 mt-1">
                                <span>Abdeckung: {Math.round(categories[hoverIdx].value * 100)}%</span>
                                <span className="text-gray-500">Chance: {Math.round((1 - categories[hoverIdx].value) * 100)}%</span>
                            </div>
                        </div>
                    )}
                </div>
                <div className="flex flex-wrap justify-center gap-3 text-[11px]">
                    {categories.map(c => {
                        const opportunity = 1 - c.value;
                        const color = opportunity > 0.8 ? 'bg-red-500' : opportunity > 0.6 ? 'bg-orange-500' : opportunity > 0.4 ? 'bg-yellow-400' : 'bg-green-500';
                        return (
                            <div key={c.name} className="flex items-center gap-1">
                                <span className={`h-2.5 w-2.5 rounded-full ${color}`} />
                                {c.name} {Math.round(opportunity * 100)}%
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    // NEW: State für Department-Auswahl & Create-Dialog
    const [createDialogOpen, setCreateDialogOpen] = useState(false);

    // Beispiel: wir nutzen die Advertisements eines (fiktiven) Sponsor-Id-Kontexts nicht,
    // sondern zeigen nur, dass hier später Backend-Daten eingebunden werden können.

    return (
        <div className="p-6 space-y-8">
            {/* Header */}
            <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
                <div>
                    <h1 className="text-3xl font-semibold text-gray-900 tracking-tight">Sponsor Discovery</h1>
                    <p className="mt-1 text-base text-gray-500">Visuelles Dashboard für Potenzial & neue Chancen</p>
                </div>
                <div className="flex gap-3">
                    <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                        <button className="px-4 py-2.5 text-sm font-semibold rounded-md bg-indigo-600 text-white shadow hover:bg-indigo-500">Export Mock</button>
                    </PermissionFeatureWrapper>
                    <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                        <button className="px-4 py-2.5 text-sm font-semibold rounded-md bg-white border border-gray-300 text-gray-700 hover:bg-gray-50">Neu bewerten</button>
                    </PermissionFeatureWrapper>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-4">
                {kpis.map(k => {
                    const color = k.trendType === 'up' ? '#16a34a' : '#dc2626';
                    const id = k.label.replace(/\s+/g, '-').toLowerCase();
                    return (
                        <div key={k.label} className="relative overflow-hidden rounded-2xl border border-gray-200 bg-white p-4 shadow-sm flex flex-col">
                            <div className="flex items-start justify-between">
                                <div>
                                    <span className="text-xs font-semibold tracking-wide text-gray-500 uppercase">{k.label}</span>
                                    <div className="mt-1.5 text-xl font-bold text-gray-800">{k.value}</div>
                                </div>
                                <span className={"text-xs font-semibold flex items-center gap-1 self-start px-2 py-1 rounded-full " + (k.trendType === 'up' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700')}>
                                    {k.trendType === 'up' ? '▲' : '▼'} {k.trend}
                                </span>
                            </div>
                            <div className="mt-2 flex-1">
                                <Sparkline data={k.spark} color={color} id={id} />
                            </div>
                        </div>
                    );
                })}
            </div>

            <div className="grid gap-8 xl:grid-cols-12">
                <div className="space-y-8 xl:col-span-8">
                    {/* Filter PanelF
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-5">
                        <div className="flex items-center justify-between">
                            <h2 className="text-base font-semibold text-gray-700">Filter & Suche</h2>
                            <button className="text-sm text-indigo-600 hover:underline font-medium">Zurücksetzen</button>
                        </div>
                        <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-600">Suche</label>
                                <input placeholder="Firma / Branche" className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none" />
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-600">Branche</label>
                                <select className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none">
                                    <option>Alle</option>
                                    <option>Energie</option>
                                    <option>IT</option>
                                    <option>Food</option>
                                    <option>Healthcare</option>
                                </select>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-600">Score ab</label>
                                <input type="range" min={0} max={100} defaultValue={60} className="w-full" />
                                <div className="text-[11px] text-gray-500">≥ 60</div>
                            </div>
                            <div className="space-y-2">
                                <label className="text-xs font-medium text-gray-600">Budget-Spanne (Mock)</label>
                                <div className="flex items-center gap-3">
                                    <input placeholder="min" className="w-24 rounded-md border border-gray-300 px-3 py-2 text-xs" />
                                    <span className="text-gray-400 text-sm">-</span>
                                    <input placeholder="max" className="w-24 rounded-md border border-gray-300 px-3 py-2 text-xs" />
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3">
                            <button className="px-4 py-2 text-sm font-semibold rounded-md bg-indigo-600 text-white hover:bg-indigo-500">Anwenden</button>
                            <button className="px-4 py-2 text-sm font-semibold rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200">Speichern</button>
                        </div>
                    </div>
*/}
                    {/* Leads Table */}
                    <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50">
                            <h2 className="text-base font-semibold text-gray-700">Top Lead-Vorschläge</h2>
                            <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span>Sortierung:</span>
                                <button className="px-3 py-1.5 rounded bg-white border border-gray-300 hover:bg-gray-100 text-xs font-medium">Score</button>
                                <button className="px-3 py-1.5 rounded bg-white border border-gray-300 hover:bg-gray-100 text-xs font-medium">Wert</button>
                            </div>
                        </div>
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="text-sm text-gray-600 bg-white">
                                    <th className="px-4 py-2 text-left font-semibold">Unternehmen</th>
                                    <th className="px-4 py-2 text-left font-semibold">Branche</th>
                                    <th className="px-4 py-2 text-left font-semibold w-40">Score</th>
                                    <th className="px-4 py-2 text-left font-semibold">Tags</th>
                                    <th className="px-4 py-2 text-left font-semibold">Ø Wert</th>
                                    <th className="px-4 py-2" />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {leads.map(l => (
                                    <tr key={l.name} className="hover:bg-indigo-50/40">
                                        <td className="px-4 py-2 font-medium text-gray-900 text-sm">{l.name}</td>
                                        <td className="px-4 py-2 text-gray-700 text-sm">{l.industry}</td>
                                        <td className="px-4 py-2">
                                            <div className="space-y-1.5">
                                                <div className="h-2.5 w-full rounded bg-gray-200 overflow-hidden">
                                                    <div className={"h-full " + scoreColor(l.score)} style={{ width: `${l.score}%` }} />
                                                </div>
                                                <div className="text-[11px] text-gray-500 font-medium">{l.score} / 100</div>
                                            </div>
                                        </td>
                                        <td className="px-4 py-2">
                                            <div className="flex flex-wrap gap-1.5">
                                                {l.tags.map(t => (
                                                    <span key={t} className="rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 px-2.5 py-0.5 text-[11px] font-medium">{t}</span>
                                                ))}
                                            </div>
                                        </td>
                                        <td className="px-4 py-2 font-semibold text-gray-800 text-sm">{l.estValue}</td>
                                        <td className="px-4 py-2 text-right">
                                            <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                                                <button className="text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 shadow font-medium">Details</button>
                                            </PermissionFeatureWrapper>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* Portfolio Lücken */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                        <h2 className="text-base font-semibold text-gray-700">Branchen-Lücken Radar</h2>
                        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {['Finanzen', 'Versicherung', 'Telekommunikation', 'Education', 'Logistik', 'Bau'].map(cat => (
                                <div key={cat} className="flex items-center justify-between rounded-lg border border-dashed border-gray-300 bg-gray-50 px-4 py-3">
                                    <span className="text-sm font-medium text-gray-700">{cat}</span>
                                    <span className="text-[11px] text-gray-500">frei</span>
                                </div>
                            ))}
                        </div>
                        <div className="py-6 flex items-center justify-center rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 text-sm text-gray-500">
                            {/* Wir ersetzen den Inhalt innerhalb der Branchen-Lücken Box */}
                            <RadarChart />
                        </div>
                    </div>

                    {/* Bereich "Branchen-Lücken Radar" bleibt, darunter fügen wir die Werbeflächen-Verwaltung ein */}

                    <div className="mt-8 rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                        <div className="flex items-center justify-between">
                            <h2 className="text-base font-semibold text-gray-700">Leistungen verwalten</h2>
                            <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                                <Button size="sm" onClick={() => setCreateDialogOpen(true)}>
                                    Neue Werbefläche anlegen
                                </Button>
                            </PermissionFeatureWrapper>
                        </div>
                        <p className="text-sm text-gray-500">
                            Hier kannst du die Leistungen definieren, die später in Sponsor-Verträgen verwendet werden.
                        </p>
                        {/* TODO: hier kann eine Tabelle/Übersicht der vorhandenen Department-Advertisements eingebunden werden */}
                    </div>

                    <CreateAdvertisementDialog
                        open={createDialogOpen}
                        onClose={() => setCreateDialogOpen(false)}
                    />
                </div>

                <div className="space-y-8 xl:col-span-4">
                    {/* Recommendations */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                        <h2 className="text-base font-semibold text-gray-700">Empfehlungen</h2>
                        <div className="space-y-3">
                            {recommendations.map(r => (
                                <div key={r.title} className="rounded-xl border border-gray-200 bg-gray-50 p-3 flex flex-col gap-2.5">
                                    <div className="flex items-start justify-between">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-800">{r.title}</span>
                                            <span className="text-[11px] text-gray-500">{r.type}</span>
                                        </div>
                                        <span className="text-sm font-semibold text-indigo-600">{r.impact}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5">
                                        {r.rationale.map(x => (
                                            <span key={x} className="px-2.5 py-0.5 rounded-full bg-white border border-gray-200 text-[11px] text-gray-600">{x}</span>
                                        ))}
                                    </div>
                                    <div className="flex gap-3">
                                        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                                            <button className="flex-1 text-xs px-3 py-1.5 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 font-semibold">Übernehmen</button>
                                        </PermissionFeatureWrapper>
                                        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                                            <button className="text-xs px-3 py-1.5 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 font-medium">Später</button>
                                        </PermissionFeatureWrapper>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Aktivierungs-Lücken */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-4">
                        <h2 className="text-base font-semibold text-gray-700">Aktivierungs-Lücken</h2>
                        <div className="space-y-3">
                            {[
                                { asset: 'Social Slot Q1', used: 2, booked: 6 },
                                { asset: 'VIP Box Spieltag 14', used: 0, booked: 1 },
                                { asset: 'Newsletter Platz', used: 1, booked: 4 },
                            ].map(a => (
                                <div key={a.asset} className="flex flex-col gap-2 rounded-lg border border-gray-200 p-3 bg-gray-50">
                                    <div className="flex items-center justify-between">
                                        <span className="text-sm font-medium text-gray-700">{a.asset}</span>
                                        <span className="text-[11px] text-gray-500">{a.used}/{a.booked} genutzt</span>
                                    </div>
                                    <div className="h-2 w-full rounded bg-gray-200 overflow-hidden">
                                        <div className="h-full bg-indigo-500" style={{ width: `${(a.used / a.booked) * 100}%` }} />
                                    </div>
                                    <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                                        <button className="self-start mt-1 text-[11px] px-3 py-1 rounded bg-white border border-indigo-200 text-indigo-600 hover:bg-indigo-50 font-medium">Aktivierung planen</button>
                                    </PermissionFeatureWrapper>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mini Szenario */}
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm space-y-3">
                        <h2 className="text-base font-semibold text-gray-700">Szenario-Simulation</h2>
                        <div className="space-y-2 text-sm text-gray-600">
                            <p>Preis-Anhebung Banden +5% → progn. +€ 6.800 Jahreswert</p>
                            <p>Kombi-Paket Social+Hospitality → +€ 12.400 Pipeline</p>
                        </div>
                        <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
                            <button className="text-xs px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-500 font-semibold">Was-wäre-wenn öffnen</button>
                        </PermissionFeatureWrapper>
                    </div>
                </div>
            </div>
        </div>
    );
}

interface CreateAdvertisementDialogProps {
  open: boolean;
  onClose: () => void;
}

const CreateAdvertisementDialog = ({ open, onClose }: CreateAdvertisementDialogProps) => {
  const [name, setName] = useState("");
  const [defaultVolume, setDefaultVolume] = useState<string>("");
  const [defaultDuration, setDefaultDuration] = useState<string>("");
  const [active, setActive] = useState(true);

  const handleSubmit = () => {
    // TODO: Backend-Call einbinden, sobald Endpoint steht
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Neue Werbefläche anlegen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="z.B. Bandenwerbung Hauptplatz" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Standard-Volumen (€)</label>
              <Input
                type="number"
                value={defaultVolume}
                onChange={(e) => setDefaultVolume(e.target.value)}
                placeholder="z.B. 5000"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Standard-Laufzeit (Tage)</label>
              <Input
                type="number"
                value={defaultDuration}
                onChange={(e) => setDefaultDuration(e.target.value)}
                placeholder="z.B. 365"
              />
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Checkbox id="active" checked={active} onCheckedChange={(checked) => setActive(!!checked)} />
            <label htmlFor="active" className="text-sm font-medium">
              Aktiv
            </label>
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={onClose}>
              Abbrechen
            </Button>
            <PermissionFeatureWrapper requiredPermission={Permission.MANAGE_ENTITIES} requiredPlan={PlanKey.BASIC}>
              <Button onClick={handleSubmit} disabled={!name}>
                Speichern
              </Button>
            </PermissionFeatureWrapper>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
